import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Camera,
  Home,
  Receipt,
  FileText,
  Bell,
  Settings,
  BarChart3,
  LayoutDashboard,
  ScrollText,
  Boxes,
  Wrench,
  Users,
  Truck,
  BookOpen,
  Banknote,
  UserCog,
  Menu,
  X,
  LogOut,
  Globe,
  HeartPulse,
  Pill as PillIcon,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useUiStore, type AppView } from '@/stores/ui';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { makeT, type Lang } from '@/lib/i18n';
import { isHealthcareBusiness, isPharmacyBusiness } from '@nako/types';
import { Pill } from '@nako/ui';
import { cn } from '@nako/ui';
import { ToothIcon } from '@/components/dental/ToothIcon';
import { ForcePasswordReset } from '@/components/ForcePasswordReset';

// English is required (used as fallback for any region language we haven't
// translated this entry into); the rest are optional and degrade to en.
type NavLabel = { en: string } & Partial<Record<Lang, string>>;

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: NavLabel;
  external?: boolean; // marks "/" — outside the /app sub-tree
}

function navText(label: NavLabel, lang: Lang): string {
  return label[lang] ?? label.en;
}

interface NavItemEx extends NavItem {
  // which app views show this item — undefined = all views
  views?: Array<'owner' | 'accountant' | 'burs'>;
  // industry gate — undefined = all industries; 'healthcare' = healthcare only
  industryGate?: 'healthcare' | 'pharmacy';
}

const NAV: NavItemEx[] = [
  { to: '/', icon: Home, label: { en: 'Home', tn: 'Legae' }, external: true },
  { to: '/app', icon: LayoutDashboard, label: { en: 'Dashboard', tn: 'Letlhakore' } },
  {
    to: '/app/patients',
    icon: HeartPulse,
    label: { en: 'Patients', tn: 'Balwetsi' },
    industryGate: 'healthcare',
  },
  {
    to: '/app/pharmacy',
    icon: PillIcon,
    label: { en: 'Pharmacy', tn: 'Khemisi' },
    industryGate: 'pharmacy',
  },
  { to: '/app/receipts', icon: Receipt, label: { en: 'Receipts', tn: 'Diresiti' } },
  { to: '/app/invoices', icon: ScrollText, label: { en: 'Invoices', tn: 'Ditshupiso' } },
  {
    to: '/app/customers',
    icon: Users,
    label: { en: 'Customers', tn: 'Bareki' },
    views: ['accountant'],
  },
  {
    to: '/app/suppliers',
    icon: Truck,
    label: { en: 'Suppliers', tn: 'Barekisi' },
    views: ['accountant'],
  },
  {
    to: '/app/inventory',
    icon: Boxes,
    label: { en: 'Inventory', tn: 'Setoko' },
    views: ['accountant'],
  },
  {
    to: '/app/assets',
    icon: Wrench,
    label: { en: 'Assets', tn: 'Dithoto' },
    views: ['accountant'],
  },
  {
    to: '/app/journal',
    icon: BookOpen,
    label: { en: 'Journal', tn: 'Tlhamalalo' },
    views: ['accountant'],
  },
  {
    to: '/app/cashbook',
    icon: Banknote,
    label: { en: 'Cashbook', tn: 'Madi' },
    views: ['accountant'],
  },
  { to: '/app/payroll', icon: UserCog, label: { en: 'Payroll', tn: 'Dituelo' } },
  { to: '/app/reports', icon: BarChart3, label: { en: 'Reports', tn: 'Dipegelo' } },
  { to: '/app/burs', icon: FileText, label: { en: 'Tax', tn: 'Lekgetho' } },
  { to: '/app/reminders', icon: Bell, label: { en: 'Reminders', tn: 'Dikgakololo' } },
  { to: '/app/settings', icon: Settings, label: { en: 'Settings', tn: 'Ditlhophiso' } },
];

// Mobile bottom nav: 5 most-used screens; Home/Inventory/Assets/Settings live
// in the side drawer to keep the bottom bar uncluttered on small phones.
const BOTTOM_NAV: NavItem[] = NAV.filter((n) =>
  ['Dashboard', 'Receipts', 'Reports', 'BURS', 'Reminders'].includes(n.label.en),
);

export function AppShell({ children }: { children: ReactNode }) {
  const drawerOpen = useUiStore((s) => s.drawerOpen);
  const setDrawer = useUiStore((s) => s.setDrawer);
  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  const lang = useUiStore((s) => s.lang);
  const setLang = useUiStore((s) => s.setLang);
  const regionCode = useRegionStore((s) => s.code);
  const regionConfig = useRegionStore((s) => s.config);
  const mode = useUiStore((s) => s.mode);
  const setMode = useUiStore((s) => s.setMode);
  const t = makeT(lang);
  const business = useAuthStore((s) => s.business);
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);
  const isAdmin = profile?.is_admin ?? false;
  const impersonating = isAdmin && business && profile && business.owner_id !== profile.id;
  const navigate = useNavigate();
  const location = useLocation();

  // Fun Mode easter egg — same 5-tap-within-1.5s logic as Welcome.
  const [logoTapTimes, setLogoTapTimes] = useState<number[]>([]);
  const onSidebarLogoTap = () => {
    if (mode === 'fun') {
      setMode('normal');
      return;
    }
    const now = Date.now();
    const recent = [...logoTapTimes.filter((t) => now - t < 1500), now];
    setLogoTapTimes(recent);
    if (recent.length >= 5) {
      setLogoTapTimes([]);
      setMode('fun');
    }
  };

  const handleSignOut = async () => {
    setDrawer(false);
    try {
      await signOut();
    } catch {
      // signOut already swallows errors internally; this is defensive only.
    }
    // Hard reload so any cached components, in-flight queries, or stale
    // auth listeners drop along with the session. location.replace also
    // prevents Back navigating into a stale authed page.
    window.location.replace('/');
  };

  const setBusinessFn = useAuthStore((s) => s.setBusiness);
  const stopViewingAs = async () => {
    sessionStorage.removeItem('np.adminViewAs');
    if (!profile) return;
    const { data: own } = await supabase
      .from('businesses')
      .select('*')
      .eq('owner_id', profile.id)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    setBusinessFn(own ?? null);
    navigate('/admin');
  };

  // Global keyboard shortcuts:
  //   Ctrl/Cmd+K   → scan receipt (the most-used action)
  //   Ctrl/Cmd+/   → toggle Setswana ↔ English
  //   Esc          → close drawer if open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      const target = e.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        navigate('/app/receipts/scan');
        return;
      }
      if (isMod && e.key === '/') {
        e.preventDefault();
        const regionLangs: Record<string, string> = {
          BW: 'tn',
          ZA: 'zu',
          NG: 'ha',
          ZW: 'sn',
        };
        const target = regionLangs[regionCode] ?? 'tn';

        setLang(lang === 'en' ? (target as any) : 'en');
        return;
      }
      if (e.key === 'Escape' && drawerOpen && !inField) {
        setDrawer(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, setLang, lang, drawerOpen, setDrawer]);

  return (
    <div className="bg-paper min-h-screen">
      <ForcePasswordReset />
      {impersonating && (
        <div className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-xs font-medium text-white">
          <span>
            ADMIN MODE — viewing as <strong>{business?.name}</strong>
          </span>
          <button
            type="button"
            onClick={() => void stopViewingAs()}
            className="rounded-full bg-white/20 px-3 py-0.5 text-white underline-offset-2 hover:bg-white/30 hover:underline"
          >
            Stop viewing as
          </button>
        </div>
      )}
      <div className="md:flex">
        {/* Sidebar (desktop) / Drawer (mobile) */}
        <aside
          className={cn(
            'md:border-line md:static md:block md:w-60 md:translate-x-0 md:border-r md:bg-white',
            'shadow-card-hover fixed inset-y-0 left-0 z-40 w-72 transform bg-white transition-transform',
            drawerOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="border-line flex items-center justify-between border-b p-4">
            <Link
              to="/app"
              onClick={() => setDrawer(false)}
              className="flex min-w-0 items-center gap-2"
            >
              {business?.logo_url ? (
                <img
                  src={business.logo_url}
                  alt=""
                  aria-hidden="true"
                  onClick={(e) => {
                    e.preventDefault();
                    onSidebarLogoTap();
                  }}
                  className="border-line h-9 w-9 shrink-0 cursor-pointer rounded-lg border object-cover"
                />
              ) : null}
              <span
                onClick={(e) => {
                  e.preventDefault();
                  onSidebarLogoTap();
                }}
                className="font-display cursor-pointer truncate text-lg font-semibold"
              >
                {business?.name ?? 'Nako Pula'}
              </span>
            </Link>
            <button
              onClick={() => setDrawer(false)}
              aria-label="Close menu"
              className="text-muted md:hidden"
            >
              <X size={20} />
            </button>
          </div>

          {business && (
            <div className="border-line border-b px-4 py-3">
              <p className="text-muted text-xs">Tier</p>
              <Pill tone={business.tier === 'free' ? 'neutral' : 'accent'} className="mt-1">
                {business.tier}
              </Pill>
              {business.industry && <p className="text-muted mt-2 text-xs">{business.industry}</p>}
            </div>
          )}

          <nav className="space-y-1 p-2">
            {NAV.filter((item) => !item.views || item.views.includes(view))
              .filter((item) => {
                if (item.industryGate === 'healthcare') {
                  return isHealthcareBusiness(business?.industry ?? null);
                }
                if (item.industryGate === 'pharmacy') {
                  return isPharmacyBusiness(business?.industry ?? null);
                }
                return true;
              })
              .map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                const isPatientsForDental =
                  item.to === '/app/patients' && /dent/i.test(business?.industry ?? '');
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setDrawer(false)}
                    className={cn(
                      // py-3 (was py-2): bigger vertical tap target on mobile
                      // to stop the "near Invoices ⇒ Payroll" misclicks.
                      'rounded-btn flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors',
                      active
                        ? isPatientsForDental
                          ? 'bg-gradient-to-r from-sky-100 to-amber-50 text-sky-900 ring-1 ring-sky-200'
                          : 'bg-paper-2 text-ink'
                        : 'text-muted hover:bg-paper-2 hover:text-ink',
                    )}
                  >
                    {isPatientsForDental ? <ToothIcon className="h-5 w-5" /> : <Icon size={18} />}
                    {item.to === '/app/burs'
                      ? regionConfig.taxAuthority
                      : navText(item.label, lang)}
                    {isPatientsForDental && !active && (
                      <span className="ml-auto inline-flex h-1.5 w-1.5 rounded-full bg-amber-400 [animation:pulse_1.6s_ease-in-out_infinite]" />
                    )}
                  </Link>
                );
              })}
          </nav>

          <div className="border-line mt-auto border-t p-2">
            <button
              onClick={() => {
                const regionLangs: Record<string, string> = {
                  BW: 'tn',
                  ZA: 'zu',
                  NG: 'ha',
                  ZW: 'sn',
                };
                const target = regionLangs[regionCode] ?? 'tn';

                setLang(lang === 'en' ? (target as any) : 'en');
              }}
              className="rounded-btn text-muted hover:bg-paper-2 hover:text-ink flex w-full items-center gap-3 px-3 py-2 text-sm"
            >
              <Globe size={18} />
              {(() => {
                const labels: Record<string, string> = {
                  BW: 'Setswana',
                  ZA: 'isiZulu',
                  NG: 'Hausa',
                  ZW: 'Shona',
                };
                return lang === 'en' ? (labels[regionCode] ?? 'Setswana') : 'English';
              })()}
            </button>
            <button
              onClick={handleSignOut}
              className="rounded-btn text-muted hover:bg-paper-2 hover:text-ink flex w-full items-center gap-3 px-3 py-2 text-sm"
            >
              <LogOut size={18} />
              Sign out
            </button>
            <p className="text-muted pb-1 pt-2 text-center text-[10px] italic">
              powered by Nako Pula
            </p>
          </div>
        </aside>

        {/* Main */}
        <div className="min-w-0 flex-1">
          {/* Top bar */}
          <header className="border-line sticky top-0 z-30 border-b bg-white/90 backdrop-blur-sm">
            <div className="flex items-center gap-2 px-4 py-3">
              <button
                onClick={() => setDrawer(true)}
                className="text-ink md:hidden"
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>

              {/* View toggle */}
              <div className="flex flex-1 justify-center md:justify-start">
                <ViewToggle current={view} onChange={setView} t={t} />
              </div>

              <Link
                to="/app/receipts/scan"
                className="rounded-btn bg-accent p-2 text-white"
                aria-label="Scan receipt"
              >
                <Camera size={20} />
              </Link>
            </div>
          </header>

          {/* Body */}
          <div className="pb-24 md:pb-6">{children}</div>

          {/* Bottom nav (mobile) */}
          <nav className="border-line fixed inset-x-0 bottom-0 z-30 border-t bg-white md:hidden">
            <div className="flex justify-around">
              {BOTTOM_NAV.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'flex flex-1 flex-col items-center gap-0.5 px-2 py-2 text-[10px]',
                      active ? 'text-accent' : 'text-muted',
                    )}
                  >
                    <Icon size={20} />
                    {item.to === '/app/burs'
                      ? regionConfig.taxAuthority
                      : navText(item.label, lang)}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </div>

      {drawerOpen && (
        <button
          aria-label="Close drawer overlay"
          className="bg-ink/40 fixed inset-0 z-30 md:hidden"
          onClick={() => setDrawer(false)}
        />
      )}
    </div>
  );
}

function ViewToggle({
  current,
  onChange,
  t,
}: {
  current: AppView;
  onChange: (v: AppView) => void;
  t: ReturnType<typeof makeT>;
}) {
  const items: { id: AppView; label: string }[] = [
    { id: 'owner', label: t('view.owner') },
    { id: 'accountant', label: t('view.accountant') },
    { id: 'burs', label: t('view.burs') },
  ];
  return (
    <div className="rounded-pill bg-paper-2 inline-flex p-0.5">
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => onChange(it.id)}
          className={cn(
            'rounded-pill px-3 py-1.5 text-xs font-medium transition-colors',
            current === it.id ? 'bg-ink text-paper' : 'text-muted',
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}
